import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { myAxios } from '../../services/Helper';
import { isValidToken, getRole } from '../../services/tokenService';
import './BookingSummaryStyles.css';

const BookingSummary = ({
    selectedEventPackage,
    occasionPrice,
    selectedDecorations,
    decorationPrice,
    selectedDateTime,
    selectedDate,
    startTime,
    endTime,
    baseTotal,
    customerInfo = {},
    eventPackageId
}) => {
    const navigate = useNavigate();
    const [equipmentItems, setEquipmentItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [orderId, setOrderId] = useState(null);

    useEffect(() => {
        const fetchEquipmentItems = async () => {
            try {
                let response;
                if (eventPackageId) {
                    response = await myAxios.get(`/api/SubfunctionCategory/items/bySubFunction1/${eventPackageId}`);
                } else {
                    response = await myAxios.get('/api/subfunctionitem/subfunctionitem');
                }
                if (!Array.isArray(response.data)) {
                    throw new Error('Invalid equipment data format');
                }
                setEquipmentItems(response.data);
                setLoading(false);
            } catch (err) {
                console.error('Error fetching equipment:', err);
                setError('Failed to load equipment information');
                setLoading(false);
            }
        };

        fetchEquipmentItems();
    }, [eventPackageId]);

    // Load Razorpay script
    useEffect(() => {
        const loadRazorpayScript = () => {
            return new Promise((resolve) => {
                const script = document.createElement('script');
                script.src = 'https://checkout.razorpay.com/v1/checkout.js';
                script.onload = () => {
                    resolve(true);
                };
                script.onerror = () => {
                    resolve(false);
                };
                document.body.appendChild(script);
            });
        };

        loadRazorpayScript();
    }, []);

    const parseTimeToSQLFormat = (timeString) => {
        if (!timeString) return null;

        try {
            // Example: "9:00 AM" or "10:30 PM"
            const [timePart, period] = timeString.split(' ');
            const [hourStr, minuteStr] = timePart.split(':');

            let hour = parseInt(hourStr, 10);
            const minute = parseInt(minuteStr, 10);

            // Convert from 12-hour to 24-hour format
            if (period === 'PM' && hour !== 12) {
                hour += 12;
            } else if (period === 'AM' && hour === 12) {
                hour = 0;
            }

            // Format as HH:MM:00 for SQL time
            const formattedHour = hour.toString().padStart(2, '0');
            const formattedMinute = minute.toString().padStart(2, '0');
            return `${formattedHour}:${formattedMinute}:00`;
        } catch (error) {
            console.error('Error parsing time:', error, timeString);
            return null;
        }
    };

    const formatDateForBackend = (dateValue) => {
        if (!dateValue) return null;

        try {
            const date = dateValue instanceof Date ? dateValue : new Date(dateValue);

            if (isNaN(date.getTime())) {
                console.error('Invalid date value:', dateValue);
                return null;
            }

            // Format as YYYY-MM-DD
            const year = date.getFullYear();
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const day = date.getDate().toString().padStart(2, '0');

            const formattedDate = `${year}-${month}-${day}`;
            console.log('Formatted date for backend:', formattedDate, 'from original value:', dateValue);
            return formattedDate;
        } catch (error) {
            console.error('Error formatting date:', error, dateValue);
            return null;
        }
    };

    const createRazorpayOrder = async () => {
        try {
            // Ensure we use the same totalAmount value for consistent pricing
            const totalAmount = Number(baseTotal) || 0;
            const amountInPaise = Math.round(totalAmount * 100);
            
            const orderData = {
                amount: amountInPaise,
                currency: "INR",
                notes: {
                    eventPackage: selectedEventPackage,
                    date: formatDateForBackend(selectedDate)
                }
            };

            const response = await myAxios.post('/api/payment/create-order', orderData);
            console.log('Order creation response:', response.data);
            
            // Try to parse the response - it could be either a JSON string or already an object
            let orderDetails;
            if (typeof response.data === 'string') {
                try {
                    orderDetails = JSON.parse(response.data);
                } catch (parseError) {
                    console.error('Error parsing order response:', parseError);
                    throw new Error('Invalid order response format');
                }
            } else {
                orderDetails = response.data;
            }
            
            console.log('Parsed order details:', orderDetails);
            return orderDetails.id; // This is the Razorpay Order ID
        } catch (error) {
            console.error('Error creating order:', error);
            alert('Error creating payment order. Please try again.');
            setIsSubmitting(false);
            throw error;
        }
    };

    const handlePayment = async (bookingData, orderId) => {
        // Ensure we use the same totalAmount value for consistent pricing
        const totalAmount = Number(baseTotal) || 0;
        
        // Create options object for Razorpay
        const options = {
            key: "rzp_test_3zJGtRaGdc9DPM", // Replace with your actual test key ID
            amount: Math.round(totalAmount * 100), // amount in paise
            currency: "INR",
            name: "Waghchaure Lawns",
            description: `Booking for ${selectedEventPackage}`,
            image: "https://shubhamjadhav.in/images/lockedme.jpg", // Replace with your actual logo URL
            order_id: orderId,
            handler: async function (response) {
                try {
                    // Verify the payment signature on the server
                    const verifyData = {
                        razorpay_order_id: response.razorpay_order_id,
                        razorpay_payment_id: response.razorpay_payment_id,
                        razorpay_signature: response.razorpay_signature,
                        booking_id: bookingData.id || bookingData.bookingId,
                        amount: Math.round(totalAmount * 100)
                    };

                    const verifyResponse = await myAxios.post('/api/payment/verify-payment', verifyData);
                    
                    console.log('Payment verification response:', verifyResponse.data);
                    
                    if (verifyResponse.data && verifyResponse.data.status === 'error') {
                        alert('Payment verification failed. Please try again.');
                        return;
                    }

                    // Update booking data with payment info
                    const updatedBookingData = {
                        ...bookingData,
                        razorpay_payment_id: response.razorpay_payment_id,
                        razorpay_order_id: response.razorpay_order_id,
                        razorpay_signature: response.razorpay_signature,
                        payment_status: 'COMPLETED',
                        status: 'CONFIRMED',
                        booking_status: 'CONFIRMED'
                    };

                    // Store the booking in session storage
                    const existingBookings = JSON.parse(sessionStorage.getItem('bookings') || '[]');
                    const updatedBookings = [updatedBookingData, ...existingBookings.filter(b => b.id !== updatedBookingData.id)];
                    sessionStorage.setItem('bookings', JSON.stringify(updatedBookings));

                    console.log('Booking updated and stored in session storage');
                    
                    // Navigate to success page
                    navigate('/booking-success', { state: { bookingData: updatedBookingData } });
                } catch (error) {
                    console.error('Error verifying payment:', error);
                    alert('Payment was successful, but verification failed. Our team will contact you shortly.');
                }
            },
            prefill: {
                name: customerInfo?.name || '',
                email: customerInfo?.email || '',
                contact: customerInfo?.phone || ''
            },
            notes: {
                address: "Your Business Address"
            },
            theme: {
                color: "#3399cc"
            },
            modal: {
                ondismiss: function () {
                    console.log('Payment modal closed');
                    setIsSubmitting(false);
                }
            }
        };

        try {
            const paymentObject = new window.Razorpay(options);
            paymentObject.open();
        } catch (error) {
            console.error('Error opening Razorpay:', error);
            alert('Could not open payment page. Please try again later.');
            setIsSubmitting(false);
        }
    };

    const handleBookingSubmission = async () => {
        if (!selectedEventPackage || !selectedDate || !startTime || !endTime) {
            alert("Please complete all required booking details.");
            return;
        }

        setIsSubmitting(true);

        try {
            const isAuthenticated = isValidToken();
            const userId = localStorage.getItem('userId');

            const formattedDate = formatDateForBackend(selectedDate);
            const formattedStartTime = parseTimeToSQLFormat(startTime);
            const formattedEndTime = parseTimeToSQLFormat(endTime);

            console.log('Selected date:', selectedDate);
            console.log('Formatted date:', formattedDate);
            console.log('Formatted start time:', formattedStartTime);
            console.log('Formatted end time:', formattedEndTime);

            // Get only the IDs from the selected decorations (which are sub-functions)
            const decorationIds = selectedDecorations
                .map(deco => deco.id || deco.decorationId)
                .filter(id => id);

            const equipmentIds = equipmentItems.map(item => item.itemId || item.id).filter(id => id);

            // For booking data, only include the sub-function IDs
            const allItemIds = [...decorationIds].filter(id => id);

            // Ensure baseTotal is a number - this is crucial
            const totalAmount = Number(baseTotal) || 0;

            if (isAuthenticated && userId) {
                const userRole = getRole();
                if (!userRole || (userRole !== 'USER' && userRole !== 'ADMIN' && userRole !== 'ROLE_USER' && userRole !== 'ROLE_ADMIN')) {
                    alert("You don't have permission to make bookings.");
                    setIsSubmitting(false);
                    return;
                }

                const bookingRequest = {
                    functionId: eventPackageId,
                    subfunctionId: allItemIds.length > 0 ? allItemIds : [],
                    booking_for_date: formattedDate,
                    start_time: formattedStartTime,
                    end_time: formattedEndTime,
                    booking_status: "PENDING",
                    payment_mode: "Online",
                    total_payment_amount: totalAmount // Note: Backend will recalculate this based on function and subfunctions
                };

                console.log('Sending booking request:', JSON.stringify(bookingRequest, null, 2));

                // First create the booking
                const response = await myAxios.post('/api/booking/book-event', bookingRequest);
                console.log('API Response:', response.data);

                // Override any existing total values in the response to ensure consistency
                const responseData = {
                    ...response.data
                };
                // Delete any existing total values that might be different
                delete responseData.totalPaymentAmount;
                delete responseData.total_payment_amount;
                delete responseData.totalAmount;
                delete responseData.total;

                const bookingResponseData = {
                    ...responseData,
                    occasion: selectedEventPackage,
                    selectedOccasion: selectedEventPackage,
                    date: formattedDate,
                    bookingForDate: formattedDate,
                    startTime: startTime,
                    endTime: endTime,
                    // Set all total properties to totalAmount to ensure consistency
                    total: totalAmount,
                    totalAmount: totalAmount,
                    totalPaymentAmount: totalAmount,
                    total_payment_amount: totalAmount,
                    status: response.data.booking_status || "PENDING"
                };

                console.log('Booking data being passed to payment:', bookingResponseData);

                // Create Razorpay order
                const orderId = await createRazorpayOrder();
                console.log('Razorpay order created:', orderId);
                
                // Process payment
                await handlePayment(bookingResponseData, orderId);

            } else {
                const bookingData = {
                    function_id: {
                        id: eventPackageId
                    },
                    booking_for_date: formattedDate,
                    start_time: formattedStartTime,
                    end_time: formattedEndTime,
                    booking_status: "PENDING",
                    payment_mode: "Online",
                    total_payment_amount: totalAmount, // Total calculated on client side
                    occasion: selectedEventPackage,
                    decorations: selectedDecorations,
                    equipment: equipmentItems
                };

                console.log('Sending guest booking data:', JSON.stringify(bookingData, null, 2));

                try {
                    const response = await myAxios.post('/api/booking/booking', bookingData);
                    console.log('Guest booking response:', response.data);

                    const bookingStatus = response.data.booking_status || "PENDING";

                    // Create a clean copy of the response data without any existing total values
                    const responseData = {
                        ...response.data
                    };
                    // Delete any existing total values that might be different
                    delete responseData.totalPaymentAmount;
                    delete responseData.total_payment_amount;
                    delete responseData.totalAmount;
                    delete responseData.total;

                    const guestBookingData = {
                        ...bookingData,
                        id: responseData.bookingID || `BK-${Date.now()}`,
                        bookingId: responseData.bookingID || `BK-${Date.now()}`,
                        date: formattedDate,
                        bookingForDate: formattedDate,
                        startTime: startTime,
                        endTime: endTime,
                        selectedOccasion: selectedEventPackage,
                        occasionPrice: Number(occasionPrice) || 0,
                        equipmentDetails: equipmentItems.map(item => ({
                            name: item.itemName,
                            id: item.itemId,
                            description: item.itemDesc
                        })),
                        // Set all total properties to totalAmount to ensure consistency
                        total: totalAmount,
                        totalAmount: totalAmount,
                        totalPaymentAmount: totalAmount,
                        total_payment_amount: totalAmount,
                        status: bookingStatus,
                        booking_status: bookingStatus,
                        customerName: customerInfo?.name || 'Guest User',
                        customerEmail: customerInfo?.email || '',
                        customerPhone: customerInfo?.phone || '',
                        bookingDate: new Date().toISOString(),
                        createdAt: new Date().toISOString(),
                        invoiceNumber: `INV-${Math.floor(100000 + Math.random() * 900000)}`
                    };

                    // Store in session for now
                    sessionStorage.setItem('currentBooking', JSON.stringify(guestBookingData));
                    
                    // Create Razorpay order
                    const orderId = await createRazorpayOrder();
                    console.log('Razorpay order created for guest:', orderId);
                    
                    // Process payment
                    await handlePayment(guestBookingData, orderId);
                    
                } catch (guestError) {
                    console.error('Error with guest booking:', guestError);
                    handleBookingError(guestError);
                }
            }
        } catch (err) {
            console.error('Error submitting booking:', err);
            handleBookingError(err);
            setIsSubmitting(false);
        }
    };

    const handleBookingError = (err) => {
        if (err.response) {
            console.error('Full error response:', err.response);
            console.error('Status code:', err.response.status);

            const errorMessage = err.response.data?.message ||
                err.response.data?.error ||
                'An error occurred during booking.';
            alert(`Booking failed: ${errorMessage}`);
        } else if (err.request) {
            console.error('No response received:', err.request);
            alert('Booking failed: No response received from server. Please check your internet connection.');
        } else {
            console.error('Error creating request:', err.message);
            alert(`Booking failed: ${err.message}`);
        }
    };

    return (
        <div className="card shadow booking-summary">
            <div className="card-body">
                <h5 className="card-title text-center mb-4">Booking Summary</h5>

                <div className="summary-section">
                    <h6 className="summary-title">Selected Event Package</h6>
                    {selectedEventPackage ? (
                        <div className="summary-item">
                            <span className="summary-label">{selectedEventPackage}</span>
                            <span className="summary-value">
                                ₹{(Number(occasionPrice) || 0).toLocaleString()}
                            </span>
                        </div>
                    ) : (
                        <div className="summary-placeholder">Not Selected</div>
                    )}

                    <h6 className="summary-title">Selected Decoration Package</h6>
                    {selectedDecorations.length > 0 ? (
                        <div className="equipment-summary">
                            {selectedDecorations.map((deco, index) => (
                                <div key={index} className="summary-item">
                                    <span className="summary-label">{deco.name}</span>
                                    <span className="summary-value">
                                        ₹{deco.price.toLocaleString()}
                                    </span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="summary-placeholder">Not Selected</div>
                    )}
                </div>

                <div className="summary-section">
                    <h6 className="summary-title">Date and Time</h6>
                    {selectedDateTime ? (
                        <div className="summary-datetime">{selectedDateTime}</div>
                    ) : (
                        <div className="summary-placeholder">Not Selected</div>
                    )}
                </div>

                {loading ? (
                    <div className="summary-section">
                        <h6 className="summary-title">Equipment</h6>
                        <div className="summary-placeholder">Loading equipment items...</div>
                    </div>
                ) : error ? (
                    <div className="summary-section">
                        <h6 className="summary-title">Equipment</h6>
                        <div className="summary-placeholder error">{error}</div>
                    </div>
                ) : equipmentItems.length > 0 ? (
                    <div className="summary-section">
                        <h6 className="summary-title">Equipment Items</h6>
                        <div className="equipment-summary">
                            {equipmentItems.map((item, index) => (
                                <div key={index} className="summary-item">
                                    <span className="summary-label">{item.itemName}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className="summary-section">
                        <h6 className="summary-title">Equipment</h6>
                        <div className="summary-placeholder">No equipment items available</div>
                    </div>
                )}

                <div className="total-section">
                    <div className="summary-item">
                        <h6 className="mb-0">Total Amount</h6>
                        <span className="total-value">
                            ₹{baseTotal.toLocaleString()}
                        </span>
                    </div>
                </div>

                <button
                    className="btn btn-primary w-100 confirm-booking-btn"
                    disabled={!selectedEventPackage || !selectedDateTime || loading || isSubmitting}
                    onClick={handleBookingSubmission}
                >
                    {isSubmitting ? 'Processing...' : loading ? 'Loading...' : 'Confirm Booking'}
                </button>
            </div>
        </div>
    );
};

export default BookingSummary;